import { http, endpoints, logger } from '@/config';
import { PRODUCT } from '@/config/endpoints';
import { REVALIDATE_TIME } from '@/constants';
import { getProductsStockGraphQl } from '@/utils/helper';

async function getProductDetails(sku: string, store_code?: string) {
  const { data } = await http.get(`${endpoints.PRODUCT}/${sku}`, {
    next: { revalidate: REVALIDATE_TIME },
    store_code,
  });
  return data;
}

async function getProductDetailsUrlKey(urlKey: string, store_code: string) {
  const date = Date.now();
  const endpoint = `products?searchCriteria[filter_groups][0][filters][0][field]=url_key&searchCriteria[filter_groups][0][filters][0][value]=${urlKey}&searchCriteria[pageSize]=1&timestamp=${date}`;
  const { data } = await http.get(endpoint, {
    store_code,
    next: { revalidate: REVALIDATE_TIME },
  });
  return data?.items[0];
}

async function getProductOptions(id: string, store_code?: string) {
  const res = await http.get(`${endpoints.PRODUCT_ATTRIBUTES}/${id}`, {
    next: { revalidate: REVALIDATE_TIME },
    store_code,
  });

  if (res?.response?.ok) {
    const options = res?.data?.options?.slice(1, res?.data?.options?.length); // remove first index as it is blank
    return {
      defaultValue: res?.data?.default_value,
      options,
    };
  }
  return null;
}

async function getMultipleProducts(
  skus: string[] | string,
  store_code?: string
) {
  const udpatedSku = typeof skus === 'string' ? skus : skus?.join(',');
  try {
    const queryParams = new URLSearchParams({
      'searchCriteria[filterGroups][0][filters][0][field]': 'sku',
      'searchCriteria[filterGroups][0][filters][0][value]': udpatedSku,
      'searchCriteria[filterGroups][0][filters][0][condition_type]': 'in',
    });
    const url = `${PRODUCT}?${queryParams.toString()}`;
    const { response, data } = await http.get(url, {
      next: { revalidate: REVALIDATE_TIME },
      store_code,
    });

    if (response?.ok) {
      return { items: data?.items };
    } else {
      logger.error(
        'API request failed:',
        response?.status,
        response?.statusText
      );
      return { items: [], error: data?.message };
    }
  } catch (error) {
    logger.error('An error occurred during the API request:', error);
    return { items: [], error: true };
  }
}

async function getConfigurableOptions(sku: string, store_code: string) {
  const res = await http.get(
    `${endpoints.PRODUCT_CONFIGURABLE}/${sku}/children`,
    {
      next: { revalidate: REVALIDATE_TIME },
      store_code,
    }
  );

  if (res?.response?.ok) {
    const sortedData = [...res.data].sort(
      (a: any, b: any) => a.custom_attributes?.size - b.custom_attributes?.size
    );
    return sortedData;
  }
  return [];
}

async function getProductStock(sku: string) {
  const res = await http.get(`${endpoints.STOCK_ITEMS}/${sku}`, {
    next: { revalidate: REVALIDATE_TIME },
  });
  if (res?.response?.ok) {
    return res?.data;
  }
  return null;
}

async function getProductStockMultiple(skus: string[], store_code?: string) {
  const request = {
    query: getProductsStockGraphQl(),
    variables: {
      skus,
    },
  };
  const res = await http.post(`${endpoints.GRAPHQL_URI}`, request, {
    cache: 'no-store',
    store_code,
  });
  if (res?.response?.ok) {
    return res?.data;
  }
  return null;
}

export {
  getProductDetails,
  getProductDetailsUrlKey,
  getProductOptions,
  getMultipleProducts,
  getConfigurableOptions,
  getProductStock,
  getProductStockMultiple,
};
